export const PENDING_SHARED_TEMPLATE_STORAGE_KEY = 'pendingSharedTemplateToken';
const SHARED_PATH = '/templates/shared';
const MAX_TOKEN_LENGTH = 512;
const TOKEN_PATTERN = /^[A-Za-z0-9._-]+$/;
// Long enough for a login + first-run gates round-trip; an abandoned token is not shown later.
export const PENDING_TOKEN_TTL_MS = 30 * 60 * 1000;

interface PendingSharedTemplate {
  token: string;
  savedAt: number;
}

const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

/** Subscribes to pending-token changes (for useSyncExternalStore). */
export const subscribePendingSharedTemplateToken = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const writePending = (value: PendingSharedTemplate | null): void => {
  try {
    if (value) sessionStorage.setItem(PENDING_SHARED_TEMPLATE_STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_SHARED_TEMPLATE_STORAGE_KEY);
  } catch {
    // Storage unavailable: the page falls back to its invalid-link state.
  }
  notify();
};

/**
 * Share links carry the token in the URL fragment (never sent to servers or logged). On landing it is
 * moved to sessionStorage and stripped from the address bar, so it survives the login round-trip
 * without ever appearing in a path, query string or `next` param. A malformed fragment clears any
 * earlier pending token so the page shows the invalid-link state rather than a previous link.
 */
export const captureSharedTemplateToken = (): void => {
  if (window.location.pathname !== SHARED_PATH) return;
  const token = window.location.hash.slice(1);
  // No fragment: a return from login or a first-run gate, keep the pending token.
  if (!token) return;
  const isValid = token.length <= MAX_TOKEN_LENGTH && TOKEN_PATTERN.test(token);
  writePending(isValid ? { token, savedAt: Date.now() } : null);
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
};

/** Captures at boot and on same-tab hash-only navigations (a second link pasted into a loaded tab). */
export const installSharedTemplateTokenCapture = (): void => {
  captureSharedTemplateToken();
  window.addEventListener('hashchange', captureSharedTemplateToken);
};

const isPending = (value: unknown): value is PendingSharedTemplate =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PendingSharedTemplate).token === 'string' &&
  typeof (value as PendingSharedTemplate).savedAt === 'number';

/** The captured token, or null when absent, unreadable or older than the TTL. */
export const readPendingSharedTemplateToken = (): string | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_SHARED_TEMPLATE_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!isPending(parsed) || Date.now() - parsed.savedAt > PENDING_TOKEN_TTL_MS) return null;
    return parsed.token;
  } catch {
    return null;
  }
};

export const clearPendingSharedTemplateToken = (): void => writePending(null);
